import { Button, Card, ToneIcon } from '@apovisio/ui';
import { ERROR_MESSAGES } from '@apovisio/contracts';
import type { ErrorCode } from '@apovisio/contracts';
import type { ReactNode } from 'react';
import { BrandMark } from '../../app/BrandMark';
import { PageHeading } from '../../components/PageHeading';
import { NETWORK_ERROR_MESSAGE } from '../../lib/api';

export type GuestEnd = ErrorCode | 'NETWORK' | 'LEFT';

interface Content {
  tone: 'info' | 'warning' | 'danger';
  title: string;
  body?: string;
}

/** Messages des parcours d'erreur invité (docs/parcours/parcours-erreur.md). */
function contentFor(reason: GuestEnd): Content {
  switch (reason) {
    case 'INVITATION_INVALID':
      return {
        tone: 'danger',
        title: 'Ce lien n’est pas valide.',
        body: 'Vérifiez qu’il est complet ou demandez un nouveau lien à votre contact Apogée.',
      };
    case 'INVITATION_EXPIRED':
      return {
        tone: 'warning',
        title: 'Ce lien a expiré.',
        body: 'Demandez un nouveau lien à votre contact Apogée.',
      };
    case 'INVITATION_REVOKED':
      return {
        tone: 'danger',
        title: 'Ce lien n’est plus actif.',
        body: 'Demandez un nouveau lien à votre contact Apogée.',
      };
    case 'MEETING_ENDED':
      return { tone: 'info', title: 'Cette visio est terminée.' };
    case 'ADMISSION_DENIED':
      return { tone: 'info', title: 'L’organisateur n’a pas accepté votre demande.' };
    case 'REMOVED_FROM_ROOM':
      return { tone: 'info', title: 'L’organisateur vous a retiré de la visio.' };
    case 'GUEST_SESSION_EXPIRED':
      return {
        tone: 'info',
        title: 'Votre session a expiré.',
        body: 'Rouvrez le lien reçu pour continuer.',
      };
    case 'LEFT':
      return {
        tone: 'info',
        title: 'Vous avez quitté la visio.',
        body: 'Rouvrez le lien reçu pour revenir.',
      };
    case 'NETWORK':
      return { tone: 'warning', title: 'Connexion impossible.', body: NETWORK_ERROR_MESSAGE };
    default:
      return { tone: 'danger', title: ERROR_MESSAGES[reason] };
  }
}

/** Page plein écran d'un état terminal (lien invalide, refus, fin de réunion…). */
export function GuestMessage({ reason, action }: { reason: GuestEnd; action?: ReactNode }) {
  const content = contentFor(reason);
  return (
    <main className="app-centered">
      <Card padding="lg" className="app-narrow" as="div">
        <BrandMark />
        <div className="av-stack av-stack--sm" role="status">
          <ToneIcon tone={content.tone} />
          <PageHeading documentTitle={content.title}>{content.title}</PageHeading>
          {content.body ? <p className="av-text-secondary">{content.body}</p> : null}
        </div>
        {action}
      </Card>
    </main>
  );
}

export function RetryButton({
  onRetry,
  label = 'Réessayer',
}: {
  onRetry: () => void;
  label?: string;
}) {
  return (
    <Button variant="secondary" onClick={onRetry}>
      {label}
    </Button>
  );
}
